import { lazy, Suspense } from "react"
import { Navigate, useRoutes } from "react-router-dom"
import LoadingPage from "components/LoadingPage"
import MainLayout from "layouts/main"
import AuthGuard from "guards/AuthGuard"
import GuestGuard from "guards/GuestGuard"

import ServicesGuard from "guards/ServicesGuard"
import ServicesLayout from "layouts/services"
import ShareViewerLayout from "layouts/shareViewer"

const Loadable = (Component) => (props) =>
  (
    <Suspense fallback={<LoadingPage />}>
      <Component {...props} />
    </Suspense>
  )

export function Router() {
  return useRoutes([
    {
      path: "auth",
      children: [
        {
          path: "login",
          element: (
            <GuestGuard>
              <Login />
            </GuestGuard>
          )
        }
      ]
    },
    {
      path: "services",
      element: (
        <ServicesGuard>
          <ServicesLayout />
        </ServicesGuard>
      ),
      children: [
        {
          path: "installation",
          element: <DeviceStatus />
        }
      ]
    },
    {
      // หน้าคนขับ — /d/<token> ไม่ต้อง login เหมือน /t/<token> · reuse ServicesGuard ตัวเดียวกัน
      // (pass-through ล้วน ๆ ไม่มี logic) · ต้องอยู่นอก /main เพราะ AuthGuard ของ main จะเด้งไปหน้า login
      // The driver page — /d/<token>, no login, same as /t/<token>. Reuses the same pass-through
      // ServicesGuard and must live outside /main, whose AuthGuard would redirect to the login page
      // เขียนเป็น path เดียว 2 segment ได้เพราะเป็น route ระดับบนสุดของ useRoutes และหน้านี้ไม่มี
      // layout ครอบ — /t ต้องแตกเป็น parent+child เพราะมี ShareViewerLayout ที่ต้อง render <Outlet/>
      // written as one two-segment path: this is a top-level route of useRoutes and the page has no
      // layout — /t needs a parent+child split because ShareViewerLayout has to render an <Outlet/>
      // QR ที่ติดหน้ารถ — คนถือมือถือส่องกล้องทั่วไปแล้วมาถึงหน้านี้ · ไม่ยิง API ไม่เปิดเผยข้อมูลรถ
      // บอกแค่ว่าต้องเปิดลิงก์คนขับก่อนแล้วสแกนจากในนั้น (token ของ QR เพียงลำพังไม่มีสิทธิ์อะไร)
      // The windscreen QR — where an ordinary phone camera lands. Calls no API, reveals nothing:
      // it only explains that the driver link must be opened first and the scan done from there
      path: "v/:token",
      element: (
        <ServicesGuard>
          <VehicleQrPage />
        </ServicesGuard>
      )
    },
    {
      path: "d/:token",
      element: (
        <ServicesGuard>
          <DriverScanPage />
        </ServicesGuard>
      )
    },
    {
      // หน้าเว็บ viewer สาธารณะ (D13) — /t/:token เท่านั้น ไม่ login, reuse ServicesGuard ตัวเดิม (pass-through
      // ล้วน ๆ ไม่มี logic) ตามที่ ticket 15 ระบุ — เขียนเพิ่มอีกไฟล์ก็ได้แค่ 3 บรรทัดเดียวกันเป๊ะ ไม่มีประโยชน์
      // Public web viewer (D13) — /t/:token only, no login. Reuses the same ServicesGuard (pure
      // pass-through, no logic) per ticket 15 — a second file would just be the same 3 lines again
      path: "t",
      element: (
        <ServicesGuard>
          <ShareViewerLayout />
        </ServicesGuard>
      ),
      children: [
        {
          path: ":token",
          element: <ShareViewer />
        }
      ]
    },
    {
      path: "main",
      element: (
        <AuthGuard>
          <MainLayout />
        </AuthGuard>
      ),
      children: [
        {
          path: "card",
          element: <Card />
        },
        {
          path: "staff",
          element: <Staff />
        },
        {
          path: "vehicle",
          element: <Vehicle />
        },
        {
          path: "driver",
          element: <Driver />
        },
        {
          path: "device",
          element: <Device />
        },
        {
          path: "bp-monitor-template",
          element: <BpMonitorTemplate />
        },
        {
          path: "firmware",
          element: <Firmware />
        },
        {
          path: "customer",
          element: <Customer />
        },
        {
          path: "trip-management",
          children: [
            {
              path: "",
              element: <TripManagement />
            },
            {
              path: "detail/:tripId",
              element: <TripDetailPage />
            },
            {
              path: "form/create",
              element: <TripManagementForm />
            },
            {
              path: "form/edit/:id",
              element: <TripManagementForm />
            },
            {
              path: "form/import",
              element: <TripImportPage />
            },
            {
              path: "advance-import",
              element: <TripAdvanceImportPage />
            },
            {
              path: "merge",
              element: <TripMergePage />
            }
          ]
        },
        {
          path: "route",
          children: [
            {
              path: "",
              element: <Route />
            },
            {
              path: "form/create",
              element: <RouteForm />
            },
            {
              path: "form/edit/:id",
              element: <RouteForm />
            }
          ]
        },
        {
          path: "location",
          children: [
            {
              path: "",
              element: <Location />
            },
            {
              path: "form/create",
              element: <LocationForm />
            },
            {
              path: "form/edit/:id",
              element: <LocationForm />
            }
          ]
        },
        {
          path: "reports",
          children: [
            {
              path: "log",
              element: <LogReport />
            }
          ]
        },
        {
          path: "dashboard",
          element: <MainDashboard />
        },
        {
          path: "tap-card-dashboard",
          element: <TapCardDashboard />
        },
        {
          path: "user",
          children: [
            {
              path: "",
              element: <User />
            },
            {
              path: "form/create",
              element: <UserForm />
            },
            {
              path: "form/edit/:id",
              element: <UserForm />
            }
          ]
        },
        {
          path: "group",
          children: [
            {
              path: "",
              element: <Group />
            },
            {
              path: "form/create",
              element: <GroupForm />
            },
            {
              path: "form/edit/:id",
              element: <GroupForm />
            }
          ]
        },
        {
          path: "permission",
          children: [
            {
              path: "",
              element: <Permission />
            },
            {
              path: "form/create",
              element: <PermissionForm />
            },
            {
              path: "form/edit/:id",
              element: <PermissionForm />
            }
          ]
        },
        {
          path: "datavisibility",
          children: [
            {
              path: "",
              element: <DataVisibility />
            }
          ]
        },
        {
          path: "transport-booking-request",
          children: [
            {
              path: "",
              element: <TransportBookingRequest />
            }
          ]
        },
        {
          path: "transport-booking-management",
          children: [
            {
              path: "",
              element: <TransportBookingManagement />
            }
          ]
        },
        {
          path: "timetable",
          element: <Timetable />
        },
        {
          path: "vehicle-type-planning",
          children: [
            {
              path: "",
              element: <VehicleTypePlanning />
            },
            {
              path: "merge",
              element: <VehicleTypePlanningMerge />
            }
          ]
        },
        {
          path: "vehicle-assignment",
          children: [
            {
              path: "",
              element: <VehicleAssignment />
            }
          ]
        },
        {
          path: "stop-parked-config",
          element: <StopParkedConfig />
        },
        {
          path: "booking-setting",
          element: <BookingSetting />
        },
        {
          path: "booking-setting/:customerId",
          element: <BookingSettingV2 />
        },
        {
          path: "customer-share-link",
          element: <CustomerShareLink />
        },
        {
          path: "alcohol-log",
          element: <AlcoholLog />
        },
        {
          path: "blood-pressure-log",
          element: <BloodPressureLog />
        },
        {
          path: "vehicle-type-rule",
          element: <VehicleTypeRule />
        },
        {
          path: "auto-merge-rule",
          element: <AutoMergeRule />
        },
        {
          path: "auto-merge-rule/form/create",
          element: <AutoMergeRuleForm />
        },
        {
          path: "auto-merge-rule/form/edit/:id",
          element: <AutoMergeRuleForm />
        },
        {
          path: "schedule-settings",
          children: [
            {
              path: "",
              element: <ScheduleSettings />
            },
            {
              path: "form/create",
              element: <ScheduleSettingsForm />
            },
            {
              path: "form/edit/:id",
              element: <ScheduleSettingsForm />
            },
            {
              path: "form/copy/:copyId",
              element: <ScheduleSettingsForm />
            }
          ]
        }
        /*
        {
          path: "transport-booking-form",
          children: [
            {
              path: "",
              element: <TransportBookingForm />
            }
          ]
        },
        */
      ]
    },
    {
      path: "*",
      children: [
        {
          path: "404",
          element: <NotFoundPage />
        },
        {
          path: "*",
          element: <Navigate to="/404" replace />
        }
      ]
    },

    {
      path: "/",
      element: (
        <GuestGuard>
          <Login />
        </GuestGuard>
      ),
      index: true
    },
    {
      path: "*",
      element: <Navigate to="/404" replace />
    }
  ])
}

// Authentication
const Login = Loadable(lazy(() => import("pages/auth/Login")))

// Card
const Card = Loadable(lazy(() => import("pages/main/Card")))

// Staff
const Staff = Loadable(lazy(() => import("pages/main/Staff")))

// Vehicle
const Vehicle = Loadable(lazy(() => import("pages/main/Vehicle")))

// Driver
const Driver = Loadable(lazy(() => import("pages/main/Driver")))

// Device
const Device = Loadable(lazy(() => import("pages/main/Device")))

// BP Monitor Template
const BpMonitorTemplate = Loadable(lazy(() => import("pages/main/BpMonitorTemplate")))

// User
const User = Loadable(lazy(() => import("pages/main/User")))
// Create User
const UserForm = Loadable(lazy(() => import("pages/main/User/components/Form")))

// // Customer
// const Customer = Loadable(lazy(() => import("pages/main/Customer")))

// // Company
// const Company = Loadable(lazy(() => import("pages/main/Company")))

// report
// logreport
const LogReport = Loadable(lazy(() => import("pages/main/reports/Log")))

// dashboard
// main dasboard
const MainDashboard = Loadable(lazy(() => import("pages/main/Dashboard")))
// tap-card-dashboard
const TapCardDashboard = Loadable(lazy(() => import("pages/main/TapCardDashboard")))

// Main
const NotFoundPage = Loadable(lazy(() => import("pages/NotFound")))

// Route
const Route = Loadable(lazy(() => import("pages/main/Route")))
// Create Route
const RouteForm = Loadable(lazy(() => import("pages/main/Route/components/Form")))

// Location
const Location = Loadable(lazy(() => import("pages/main/Location")))
const LocationForm = Loadable(lazy(() => import("pages/main/Location/components/Form")))

// Permission
const Permission = Loadable(lazy(() => import("pages/main/Permission")))
// Create Permission
const PermissionForm = Loadable(lazy(() => import("pages/main/Permission/components/Form")))

// Group
const Group = Loadable(lazy(() => import("pages/main/Group")))
// Create Group
const GroupForm = Loadable(lazy(() => import("pages/main/Group/components/Form")))

// firmware
const Firmware = Loadable(lazy(() => import("pages/main/Firmware")))

// Services

// device status
const DeviceStatus = Loadable(lazy(() => import("pages/services/Installation")))

// Trip Management
const TripManagement = Loadable(lazy(() => import("pages/main/TripManagement")))
//  Form trip management
const TripManagementForm = Loadable(lazy(() => import("pages/main/TripManagement/components/FormManageTrip")))

const TripDetailPage = Loadable(lazy(() => import("pages/main/TripManagement/TripDetailPage")))
// Merge trips page
const TripMergePage = Loadable(lazy(() => import("pages/main/TripManagement/TripMergePage")))
// Import trip from Excel page
const TripImportPage = Loadable(lazy(() => import("pages/main/TripManagement/ImportTrip")))
// Advance trip import page (an Excel list of rounds → trips over a date range · TECH_SPEC_ROUND3 4a)
// หน้านำเข้าทริปล่วงหน้า (รายการรอบรถจาก Excel → ทริปตลอดช่วงวันที่ · TECH_SPEC_ROUND3 4a)
const TripAdvanceImportPage = Loadable(lazy(() => import("pages/main/TripManagement/components/AdvanceTripImport")))

// customer
const Customer = Loadable(lazy(() => import("pages/main/Customer")))

// datavisibility
const DataVisibility = Loadable(lazy(() => import("pages/main/DataVisibility")))

// Transport Booking Request
const TransportBookingRequest = Loadable(lazy(() => import("pages/main/StaffShuttle/TransportBookingRequest")))

// Transport Booking Assign Vehicle
const TransportBookingManagement = Loadable(lazy(() => import("pages/main/StaffShuttle/TransportBookingManagement")))

// Transport Booking Form
const TransportBookingForm = Loadable(lazy(() => import("pages/main/StaffShuttle/TransportBookingForm")))

// Timetable
const Timetable = Loadable(lazy(() => import("pages/main/Timetable")))

// Vehicle Type Planning (จัดประเภทรถ)
const VehicleTypePlanning = Loadable(lazy(() => import("pages/main/StaffShuttle/VehicleTypePlanning")))
const VehicleTypePlanningMerge = Loadable(lazy(() => import("pages/main/StaffShuttle/VehicleTypePlanning/MergePage")))

// Vehicle Assignment (จัดรถ)
const VehicleAssignment = Loadable(lazy(() => import("pages/main/StaffShuttle/VehicleAssignment")))

// Stop Parked Config (ตั้งค่าเงื่อนไขตรวจจับการจอดจริง)
const StopParkedConfig = Loadable(lazy(() => import("pages/main/StopParkedConfig")))

// Booking Setting (ตั้งค่าการจอง — ช่วงเปิดรับจองต่อลูกค้า)
const BookingSetting = Loadable(lazy(() => import("pages/main/BookingSetting")))
const BookingSettingV2 = Loadable(lazy(() => import("pages/main/BookingSetting/v2")))
const CustomerShareLink = Loadable(lazy(() => import("pages/main/CustomerShareLink")))
const ShareViewer = Loadable(lazy(() => import("pages/shareViewer")))
const DriverScanPage = Loadable(lazy(() => import("pages/driverScan")))
const VehicleQrPage = Loadable(lazy(() => import("pages/vehicleQr")))

// Alcohol Log (ประวัติการเป่าแอลกอฮอล)
const AlcoholLog = Loadable(lazy(() => import("pages/main/AlcoholLog")))
const BloodPressureLog = Loadable(lazy(() => import("pages/main/BloodPressureLog")))

// Vehicle Type Rule (ตั้งค่าเงื่อนไขแนะนำประเภทรถตามจำนวน pax ต่อ route)
const VehicleTypeRule = Loadable(lazy(() => import("pages/main/VehicleTypeRule")))
const AutoMergeRule = Loadable(lazy(() => import("pages/main/AutoMergeRule")))
const AutoMergeRuleForm = Loadable(lazy(() => import("pages/main/AutoMergeRule/components/FormPage")))

// Schedule Settings (ตั้งค่าการเดินรถ)
const ScheduleSettings = Loadable(lazy(() => import("pages/main/ScheduleSettings")))
const ScheduleSettingsForm = Loadable(lazy(() => import("pages/main/ScheduleSettings/components/Form")))
